import { FC, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { DocumentTypeEnumType } from "@libs/api/patients";
import { DocumentCard } from "@/blocks/DocumentCard";
import { DocumentTypeHints } from "@/blocks/DocumentTypeHints";
import { DocumentTypeSelectDialog } from "@/blocks/DocumentTypeSelectDialog";
import { UploadButton } from "@/blocks/UploadButton";
import {
  VnAlertDialog,
  VnAlertDialogAction,
  VnAlertDialogCancel,
  VnAlertDialogContent,
  VnAlertDialogDescription,
  VnAlertDialogFooter,
  VnAlertDialogHeader,
  VnAlertDialogTitle,
} from "@/components/VnAlertDialog";
import { VnBox } from "@/components/VnBox";
import { VnScrollView } from "@/components/VnScrollView";
import { VnSheetToastContainer } from "@/components/VnSheetToast";
import { useDeleteDocumentMutation } from "@/data/mutations/uploadedDocumentsMutations";
import { useAddWorkRequirementDocumentMutation } from "@/data/mutations/workRequirementsMutations";
import { useListDocumentCategoriesQuery } from "@/data/queries/uploadedDocumentsQueries";
import { WorkRequirementsQueryKeys } from "@/data/queries/workRequirementsQueries";
import { documentTypes } from "@/domain/uploadedDocuments";
import { WorkRequirementSlotType } from "@/domain/workRequirements";
import { useFilePicker } from "@/hooks/useFilePicker";
import { useSheetToast } from "@/hooks/useSheetToast";
import { useTranslator } from "@/hooks/useTranslate";
import { useUploadDocument } from "@/hooks/useUploadDocument";
import { logger } from "@/utils/logger";

type WorkRequirementProofSheetPropsType = {
  workRequirementId: string;
  householdMemberId: string;
  slot: WorkRequirementSlotType;
};

// Uploads proof for one work requirement slot (a month of work or the
// exemption) and links each upload to that slot, so documents only count
// toward the month they were uploaded for.
export const WorkRequirementProofSheet: FC<
  WorkRequirementProofSheetPropsType
> = ({ workRequirementId, householdMemberId, slot }) => {
  const { t } = useTranslator();
  const sheetToast = useSheetToast();
  const queryClient = useQueryClient();
  const documentGroup = slot.kind;

  const [selectedDocumentType, setSelectedDocumentType] =
    useState<DocumentTypeEnumType | null>(null);
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [documentIdToDelete, setDocumentIdToDelete] = useState<string | null>(
    null,
  );

  const { data: documentCategories } = useListDocumentCategoriesQuery();
  const { pickFiles } = useFilePicker();
  const { pendingUploads, uploadDocuments, isUploading } = useUploadDocument({
    householdMemberId,
  });
  const { mutateAsync: addWorkRequirementDocument } =
    useAddWorkRequirementDocumentMutation();
  const {
    mutate: deleteDocument,
    mutateAsync: deleteDocumentAsync,
    isPending: isDeletingDocument,
  } = useDeleteDocumentMutation({ householdMemberId, documentGroup });

  const groupCategories = useMemo(
    () =>
      (documentCategories ?? []).filter(
        category => category.group === documentGroup,
      ),
    [documentCategories, documentGroup],
  );

  const documentTypeOptions = useMemo(
    () =>
      groupCategories.flatMap(
        category =>
          documentTypes.find(type => type.value === category.type) ?? [],
      ),
    [groupCategories],
  );

  const slotDocumentIds = new Set(slot.documents.map(({ id }) => id));
  const visiblePendingUploads = pendingUploads.filter(
    upload => !upload.documentId || !slotDocumentIds.has(upload.documentId),
  );

  // A document that can't be linked would never count toward the slot and
  // the patient couldn't see it anywhere, so it's removed and the upload fails.
  const linkToSlot = async (householdMemberDocumentId: string) => {
    try {
      await addWorkRequirementDocument({
        workRequirementId,
        householdMemberId,
        householdMemberDocumentId,
        coverageMonth: slot.coverageMonth,
      });
    } catch (error) {
      await deleteDocumentAsync(householdMemberDocumentId).catch(
        (cleanupError: unknown) => {
          logger.error({
            error: cleanupError,
            message: "Failed to remove an unlinked work requirement document",
          });
        },
      );
      throw error;
    }
  };

  const handleDocumentTypeSelect = async () => {
    setIsTypeDialogOpen(false);

    const category = groupCategories.find(
      ({ type }) => type === selectedDocumentType,
    );
    setSelectedDocumentType(null);
    if (!category) return;

    const result = await pickFiles();
    if (!result.isSuccess || result.files.length === 0) return;

    await uploadDocuments({
      files: result.files,
      documentCategoryId: category.id,
      documentType: category.type,
      onDocumentAdded: linkToSlot,
    });
  };

  const handleDelete = () => {
    if (!documentIdToDelete) return;

    deleteDocument(documentIdToDelete, {
      onSuccess: () => {
        setDocumentIdToDelete(null);
        void queryClient.invalidateQueries({
          queryKey: WorkRequirementsQueryKeys.all,
        });
        sheetToast.success(t("native:applicantDocuments.deleteToast.success"));
      },
      onError: () => {
        setDocumentIdToDelete(null);
        sheetToast.error(t("native:applicantDocuments.deleteToast.error"));
      },
    });
  };

  return (
    <>
      <DocumentTypeHints
        documentGroup={documentGroup}
        documentTypeOptions={documentTypeOptions}
      />

      <VnSheetToastContainer
        toasts={sheetToast.toasts}
        onDismiss={id => sheetToast.dismissToast(id)}
      />
      <VnBox className="flex flex-1 gap-5 pt-5">
        <UploadButton
          onPress={() => setIsTypeDialogOpen(true)}
          isUploading={isUploading}
        />
        <VnScrollView showsVerticalScrollIndicator={false}>
          {visiblePendingUploads.map(upload => (
            <DocumentCard
              key={upload.id}
              documentType={upload.documentType}
              status={upload.status}
              updatedAt={upload.updatedAt}
            />
          ))}
          {slot.documents.map(document => (
            <DocumentCard
              key={document.id}
              documentType={document.category.type}
              status="UPLOADED"
              updatedAt={document.updatedAt}
              onDelete={() => setDocumentIdToDelete(document.id)}
            />
          ))}
        </VnScrollView>
      </VnBox>

      <DocumentTypeSelectDialog
        isOpen={isTypeDialogOpen}
        onOpenChange={setIsTypeDialogOpen}
        documentTypeOptions={documentTypeOptions}
        selectedDocumentType={selectedDocumentType}
        onSelectDocumentType={setSelectedDocumentType}
        onNext={() => {
          void handleDocumentTypeSelect();
        }}
      />

      <VnAlertDialog
        open={Boolean(documentIdToDelete)}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setDocumentIdToDelete(null);
          }
        }}>
        <VnAlertDialogContent>
          <VnAlertDialogHeader>
            <VnAlertDialogTitle i18nKey="native:applicantDocuments.deleteDialog.title" />
            <VnAlertDialogDescription i18nKey="native:applicantDocuments.deleteDialog.description" />
          </VnAlertDialogHeader>
          <VnAlertDialogFooter>
            <VnAlertDialogCancel i18nKey="common:cancel" />
            <VnAlertDialogAction
              variant="destructive"
              i18nKey="common:delete"
              isLoading={isDeletingDocument}
              onPress={handleDelete}
            />
          </VnAlertDialogFooter>
        </VnAlertDialogContent>
      </VnAlertDialog>
    </>
  );
};
